import React, { useEffect, useState } from "react";
import { Button, Card, Col, Dropdown, Form, Nav, Row, Spinner, Tab } from "react-bootstrap";
import Swal from "sweetalert2";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import ChangePasswordCard from "../ChangePasswordCard";

const AgentProfile = () => {
  const { bearerAuth, publicAuth } = useApi();
  const { updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    username: "",
    phone: "",
    address: "",
    city_ids: [],
    bank_name: "",
    account_holder_name: "",
    account_number: "",
    ifsc: "",
    branch: "",
    upi_id: "",
    photo: "",
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, citiesRes] = await Promise.all([
        bearerAuth("agent/profile"),
        publicAuth("cities"),
      ]);

      const data = profileRes?.data?.data || {};
      setForm({
        name: data.name || "",
        email: data.email || "",
        username: data.username || "",
        phone: data.phone || "",
        address: data.address || "",
        city_ids: Array.isArray(data.city_ids)
          ? data.city_ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
          : [],
        bank_name: data.bank_name || "",
        account_holder_name: data.account_holder_name || "",
        account_number: data.account_number || "",
        ifsc: data.ifsc || "",
        branch: data.branch || "",
        upi_id: data.upi_id || "",
        photo: data.photo || "",
      });

      setCities(citiesRes?.data?.data || []);
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to load profile.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getCityLabel = (city) => (city.state_name ? `${city.name}, ${city.state_name}` : city.name);

  const onCityToggle = (cityId) => {
    setForm((prev) => {
      const selected = prev.city_ids || [];
      const isSelected = selected.includes(cityId);
      return {
        ...prev,
        city_ids: isSelected ? selected.filter((id) => id !== cityId) : [...selected, cityId],
      };
    });
  };

  const selectedCityIds = form.city_ids || [];
  const selectedCityNames = cities
    .filter((city) => selectedCityIds.includes(Number(city.id)))
    .map((city) => getCityLabel(city));
  const selectedCitiesLabel = !selectedCityNames.length
    ? "Select cities"
    : selectedCityNames.length <= 2
      ? selectedCityNames.join(", ")
      : `${selectedCityNames.slice(0, 2).join(", ")} +${selectedCityNames.length - 2} more`;

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        address: form.address,
        city_ids: form.city_ids,
        bank_name: form.bank_name,
        account_holder_name: form.account_holder_name,
        account_number: form.account_number,
        ifsc: form.ifsc,
        branch: form.branch,
        upi_id: form.upi_id,
      };

      const res = await bearerAuth("agent/profile", "POST", payload);
      if (res?.data?.status) {
        updateUser({ ...res.data.data });
      }
      Swal.fire("Success", "Profile updated successfully.", "success");
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to update profile.", "error");
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="container-fluid">
      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0">Agent Profile</h5>
            <small className="text-muted">Complete both tabs to keep your profile ready for payouts.</small>
          </div>
          
          {/* Use Tab.Container without wrapping in Form */}
          <Tab.Container defaultActiveKey="personal">
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="personal">Personal Details</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="bank">Bank Details</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="security">Security</Nav.Link>
              </Nav.Item>
            </Nav>
            
            <Tab.Content>
              <Tab.Pane eventKey="personal">
                <Form onSubmit={saveProfile}>
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <Row className="g-3">
                        <Col lg={4} md={6}><Form.Label>Name</Form.Label><Form.Control required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Col>
                        <Col lg={4} md={6}><Form.Label>Username</Form.Label><Form.Control value={form.username} disabled /></Col>
                        <Col lg={4} md={6}><Form.Label>Email</Form.Label><Form.Control value={form.email} disabled /></Col>
                        <Col lg={4} md={6}><Form.Label>Phone</Form.Label><Form.Control required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Col>
                        <Col lg={8} md={6}><Form.Label>Address</Form.Label><Form.Control value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Col>
                        <Col md={12}>
                          <Form.Label>Operating Cities</Form.Label>
                          <Dropdown autoClose="outside">
                            <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start">
                              <span className="d-inline-block text-truncate" style={{ maxWidth: "100%" }}>
                                {selectedCitiesLabel}
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="w-100 p-2" style={{ maxHeight: "260px", overflowY: "auto" }}>
                              {cities.length ? (
                                cities.map((city) => {
                                  const cityId = Number(city.id);
                                  return (
                                    <Form.Check
                                      key={city.id}
                                      id={`agent-city-${city.id}`}
                                      className="mb-2"
                                      type="checkbox"
                                      label={getCityLabel(city)}
                                      checked={selectedCityIds.includes(cityId)}
                                      onChange={() => onCityToggle(cityId)}
                                    />
                                  );
                                })
                              ) : (
                                <div className="text-muted small">No cities available.</div>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                          <Form.Text className="text-muted">Select one or more cities.</Form.Text>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  <div className="mt-3">
                    <Button type="submit">Save Profile</Button>
                  </div>
                </Form>
              </Tab.Pane>
              
              <Tab.Pane eventKey="bank">
                <Form onSubmit={saveProfile}>
                  <Card className="border-0 bg-light">
                    <Card.Body>
                      <Row className="g-3">
                        <Col lg={4} md={6}><Form.Label>Bank Name</Form.Label><Form.Control value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></Col>
                        <Col lg={4} md={6}><Form.Label>Account Holder</Form.Label><Form.Control value={form.account_holder_name} onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })} /></Col>
                        <Col lg={4} md={6}><Form.Label>Account Number</Form.Label><Form.Control value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></Col>
                        <Col lg={4} md={6}><Form.Label>IFSC</Form.Label><Form.Control value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} /></Col>
                        <Col lg={4} md={6}><Form.Label>Branch</Form.Label><Form.Control value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></Col>
                        <Col lg={4} md={6}><Form.Label>UPI ID</Form.Label><Form.Control value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} /></Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  <div className="mt-3">
                    <Button type="submit">Save Profile</Button>
                  </div>
                </Form>
              </Tab.Pane>
              
              <Tab.Pane eventKey="security">
                {/* No form wrapper here - ChangePasswordCard has its own form */}
                <ChangePasswordCard endpoint="change-password" />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AgentProfile;